import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { chat as chatRequest, escalate as escalateRequest } from '../api/assistantApi';

/** Events the backend script engine understands (`backend/app/services/assistant/models.py`). */
export const ASSISTANT_EVENT = Object.freeze({
    greeting: 'greeting',
    userMessage: 'user_message',
    optionSelected: 'option_selected',
    stepEntered: 'step_entered',
    wizardChanged: 'wizard_changed',
    idle: 'idle',
});

/** Turns the user is actively waiting for; only these surface a failure bubble. */
const USER_INITIATED_EVENTS = new Set([ASSISTANT_EVENT.userMessage, ASSISTANT_EVENT.optionSelected]);

/** The "..." bubble stays up at least this long before an assistant message that will be shown. */
export const ASSISTANT_TYPING_MIN_MS = 1000;
/** Silence after the last assistant message before Spark nudges (only with no open question). */
export const ASSISTANT_IDLE_MS = 30_000;
/** Wizard edits are coalesced before they are reported. */
export const ASSISTANT_WIZARD_CHANGED_DEBOUNCE_MS = 600;

export const ASSISTANT_REQUEST_FAILED_MESSAGE =
    "Sorry, I couldn't reach the assistant just now. Please try again in a moment.";

let idCounter = 0;
const nextId = (prefix) => `${prefix}-${Date.now().toString(36)}-${(idCounter += 1)}`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Transcript as the backend wants it: `{role, content}` only. */
const toWireMessages = (messages) =>
    messages.map(({ role, content }) => ({ role, content }));

/** True while the newest message is the assistant's, i.e. the user has not answered it yet. */
export const isWaitingOnUser = (messages) =>
    messages.length > 0 && messages[messages.length - 1].role === 'assistant';

/**
 * Conversation state for the Spark wizard assistant.
 *
 * Spark speaks on its own only once: the greeting. Every later background
 * turn (`wizard_changed`, `step_entered`, `idle`) is shown only when the user
 * has answered the assistant's newest message in the chat; otherwise it is a
 * quiet state sync (see `runTurn`). A user who never writes sees just the
 * greeting, however far they take the wizard by hand.
 *
 * @param {object} params
 * @param {() => object} params.getWizardState Latest `WizardState` snapshot for the backend.
 * @param {(actions: Array<{type: string, payload?: object}>) => (void | Promise<void>)} params.applyActions
 *   Applies the turn's wizard actions (everything except `escalate`, handled here).
 * @param {boolean} [params.enabled=true] When false nothing is sent (panel closed).
 * @param {object} [params.timings] Test overrides for the typing / idle / debounce delays.
 */
export function useSparkAssistant({ getWizardState, applyActions, enabled = true, timings = {} }) {
    const typingMinMs = timings.typingMinMs ?? ASSISTANT_TYPING_MIN_MS;
    const idleMs = timings.idleMs ?? ASSISTANT_IDLE_MS;
    const wizardChangedDebounceMs =
        timings.wizardChangedDebounceMs ?? ASSISTANT_WIZARD_CHANGED_DEBOUNCE_MS;

    const [messages, setMessages] = useState([]);
    const [pendingQuestion, setPendingQuestion] = useState(null);
    const [pendingContext, setPendingContext] = useState({});
    const [loading, setLoading] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const [offerSupport, setOfferSupport] = useState(false);

    // Refs mirror state the async turn needs without stale closures.
    // `messagesRef` is the source of truth; `messages` state mirrors it for rendering.
    const messagesRef = useRef(messages);
    const pendingQuestionRef = useRef(pendingQuestion);
    pendingQuestionRef.current = pendingQuestion;
    const pendingContextRef = useRef(pendingContext);
    pendingContextRef.current = pendingContext;
    const getWizardStateRef = useRef(getWizardState);
    getWizardStateRef.current = getWizardState;
    const applyActionsRef = useRef(applyActions);
    applyActionsRef.current = applyActions;
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const inFlightRef = useRef(false);
    const queuedRef = useRef(null); // latest deferred `{event, extra}` while a turn is in flight
    const mountedRef = useRef(true);
    const greetedRef = useRef(false);
    const idleTimerRef = useRef(null);
    const debounceTimerRef = useRef(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimeout(idleTimerRef.current);
            clearTimeout(debounceTimerRef.current);
        };
    }, []);

    const clearIdleTimer = useCallback(() => {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
    }, []);

    const appendMessage = useCallback((message) => {
        messagesRef.current = [...messagesRef.current, { id: nextId(message.role), ...message }];
        setMessages(messagesRef.current);
    }, []);

    /**
     * At most one "couldn't reach the assistant" bubble in the transcript: a
     * repeat failure moves it under the user's latest message instead of
     * stacking another copy.
     */
    const appendFailure = useCallback(() => {
        messagesRef.current = messagesRef.current.filter(
            (m) => !(m.role === 'assistant' && m.content === ASSISTANT_REQUEST_FAILED_MESSAGE)
        );
        appendMessage({ role: 'assistant', content: ASSISTANT_REQUEST_FAILED_MESSAGE });
    }, [appendMessage]);

    // Forward-declared so `armIdleTimer` can call it.
    const runTurnRef = useRef(null);

    /**
     * (Re)start the idle nudge; a no-op while a question is waiting for the
     * user or the assistant's last message is unanswered (the nudge would be
     * a second message in a row, which Spark never sends on its own).
     */
    const armIdleTimer = useCallback(() => {
        clearIdleTimer();
        if (!enabledRef.current || !idleMs) return;
        if (pendingQuestionRef.current || isWaitingOnUser(messagesRef.current)) return;
        idleTimerRef.current = setTimeout(() => {
            idleTimerRef.current = null;
            if (!mountedRef.current || !enabledRef.current) return;
            if (pendingQuestionRef.current || isWaitingOnUser(messagesRef.current)) return;
            runTurnRef.current?.(ASSISTANT_EVENT.idle, {});
        }, idleMs);
    }, [clearIdleTimer, idleMs]);

    /**
     * Call `/assistant/escalate` with the transcript and append Spark's
     * confirmation once, carrying `options` (the question resumed by the
     * chat turn) so the user can pick up where the off-topic detour started.
     * An older backend that still says the confirmation in the chat turn
     * does not get a second copy.
     */
    const escalate = useCallback(
        async (options = []) => {
            const wizardState = getWizardStateRef.current?.() || {};
            try {
                const result = await escalateRequest({
                    messages: toWireMessages(messagesRef.current),
                    usecase_id: wizardState.usecase_id ?? null,
                    step: wizardState.step ?? null,
                });
                if (!mountedRef.current) return;
                setOfferSupport(false);
                const last = messagesRef.current.at(-1);
                const alreadySaid = last?.role === 'assistant' && last.content === result?.message;
                if (result?.message && !alreadySaid) {
                    appendMessage({ role: 'assistant', content: result.message, options });
                }
            } catch {
                if (!mountedRef.current) return;
                appendFailure();
            }
        },
        [appendFailure, appendMessage]
    );

    /**
     * One request/response cycle. `pending_question` and `pending_context` are
     * echoed back exactly as the backend returned them; the frontend never
     * interprets or rewrites them.
     */
    const runTurn = useCallback(
        async (event, extra = {}) => {
            if (!enabledRef.current) return;
            if (inFlightRef.current) {
                // Background signals are coalesced: keep only the latest one.
                // User-initiated events are never dropped — they just wait.
                if (event === ASSISTANT_EVENT.wizardChanged || event === ASSISTANT_EVENT.idle) {
                    if (!queuedRef.current || queuedRef.current.event !== ASSISTANT_EVENT.userMessage) {
                        queuedRef.current = { event, extra };
                    }
                    return;
                }
                queuedRef.current = { event, extra };
                return;
            }
            inFlightRef.current = true;
            clearIdleTimer();
            // Only a turn the user is waiting on (or the greeting) shows "..."
            // up front. Background turns (wizard edits, step changes, idle)
            // often say nothing; showing the bubble then disables the options
            // that are already on screen.
            const expectsReply = event === ASSISTANT_EVENT.greeting || USER_INITIATED_EVENTS.has(event);
            // Spark never writes two messages in a row on its own: a background
            // turn that starts while the user has not answered the last
            // assistant message stays quiet. The reply's dialogue state is
            // still kept (so the next user turn continues from the question
            // the wizard is at), but no bubble is shown and no wizard action
            // runs. Decided before the request so a message the user types
            // while it is in flight gets its own answer, not this one.
            const quiet = !expectsReply && isWaitingOnUser(messagesRef.current);
            if (expectsReply) setLoading(true);
            const startedAt = Date.now();

            const payload = {
                messages: toWireMessages(messagesRef.current),
                wizard_state: getWizardStateRef.current?.() || {},
                pending_question: pendingQuestionRef.current,
                pending_context: pendingContextRef.current || {},
                event,
                selected_option: extra.selectedOption ?? null,
            };

            let response = null;
            let failed = false;
            try {
                response = await chatRequest(payload);
            } catch {
                failed = true;
            }

            const holdTyping = async () => {
                const elapsed = Date.now() - startedAt;
                if (elapsed < typingMinMs) await sleep(typingMinMs - elapsed);
            };
            const stillMounted = () => mountedRef.current;

            if (!stillMounted()) {
                inFlightRef.current = false;
                return;
            }

            if (failed || !response) {
                // Keep a bubble the user already sees up for the minimum, then
                // drop it. A background failure never showed one.
                if (expectsReply) await holdTyping();
                if (!stillMounted()) {
                    inFlightRef.current = false;
                    return;
                }
                setLoading(false);
                // Only answer a failure the user is waiting for. Background turns
                // (greeting, step/wizard changes, idle nudges) fail silently so the
                // chat never pulls attention away from the wizard on its own.
                if (USER_INITIATED_EVENTS.has(event)) {
                    appendFailure();
                } else {
                    console.warn(`[spark-assistant] ${event} turn failed`);
                }
            } else {
                // Echo the backend's dialogue state verbatim.
                setPendingQuestion(response.pending_question ?? null);
                setPendingContext(response.pending_context || {});
                pendingQuestionRef.current = response.pending_question ?? null;
                pendingContextRef.current = response.pending_context || {};
                setUnavailable(Boolean(response.assistant_unavailable));
                setOfferSupport(Boolean(response.offer_support));
                const spoke = Boolean(response.message) && !quiet;
                if (spoke) {
                    // A background turn that does speak shows "..." only for the
                    // time still left in the minimum, and only when some is left.
                    // A turn with no message never shows it. A request that
                    // already took the minimum shows the text immediately.
                    const remaining = typingMinMs - (Date.now() - startedAt);
                    if (remaining > 0) {
                        if (!expectsReply) setLoading(true);
                        await sleep(remaining);
                    }
                    if (!stillMounted()) {
                        inFlightRef.current = false;
                        return;
                    }
                    setLoading(false);
                    appendMessage({
                        role: 'assistant',
                        content: response.message,
                        options: Array.isArray(response.options) ? response.options : [],
                    });
                }
                const actions = quiet || !Array.isArray(response.actions) ? [] : response.actions;
                const escalating = actions.some((action) => action?.type === 'escalate');
                // A silent escalation turn keeps "..." up until /escalate
                // confirms, so "Send to support" is never followed by nothing.
                const holdForEscalate = escalating && expectsReply && !spoke;
                if (!spoke && !holdForEscalate) setLoading(false);
                const wizardActions = actions.filter((action) => action?.type !== 'escalate');
                if (wizardActions.length) {
                    try {
                        await applyActionsRef.current?.(wizardActions);
                    } catch (err) {
                        console.warn('[spark-assistant] applying actions failed', err);
                    }
                }
                if (escalating) {
                    // The chat turn said nothing of its own, so the resumed
                    // question's options ride on the confirmation bubble.
                    const resumed = !spoke && Array.isArray(response.options) ? response.options : [];
                    if (holdForEscalate) await holdTyping();
                    await escalate(resumed);
                    if (stillMounted()) setLoading(false);
                }
            }

            inFlightRef.current = false;
            if (!mountedRef.current) return;
            armIdleTimer();

            const queued = queuedRef.current;
            queuedRef.current = null;
            if (queued) {
                runTurn(queued.event, queued.extra);
            }
        },
        [appendFailure, appendMessage, armIdleTimer, clearIdleTimer, escalate, typingMinMs]
    );
    runTurnRef.current = runTurn;

    // Greeting: once, on the first mount of an empty chat.
    useEffect(() => {
        if (!enabled || greetedRef.current) return;
        if (messagesRef.current.length) return;
        greetedRef.current = true;
        runTurn(ASSISTANT_EVENT.greeting, {});
    }, [enabled, runTurn]);

    const sendMessage = useCallback(
        (text) => {
            const content = (text ?? '').trim();
            if (!content || !enabledRef.current) return;
            clearIdleTimer();
            clearTimeout(debounceTimerRef.current);
            const message = { id: nextId('user'), role: 'user', content };
            messagesRef.current = [...messagesRef.current, message];
            setMessages(messagesRef.current);
            runTurn(ASSISTANT_EVENT.userMessage, {});
        },
        [clearIdleTimer, runTurn]
    );

    const selectOption = useCallback(
        (option) => {
            if (!option || !enabledRef.current) return;
            clearIdleTimer();
            clearTimeout(debounceTimerRef.current);
            const message = {
                id: nextId('user'),
                role: 'user',
                content: option.label ?? String(option.id),
                optionId: option.id,
            };
            messagesRef.current = [...messagesRef.current, message];
            setMessages(messagesRef.current);
            runTurn(ASSISTANT_EVENT.optionSelected, { selectedOption: option.id });
        },
        [clearIdleTimer, runTurn]
    );

    /**
     * Wizard-side signals. `wizard_changed` is debounced and resets the idle
     * timer (the user is busy); `step_entered` goes out immediately; `idle` is
     * only accepted while no question is pending.
     */
    const notifyEvent = useCallback(
        (event, extra = {}) => {
            if (!enabledRef.current) return;
            if (event === ASSISTANT_EVENT.wizardChanged) {
                armIdleTimer();
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = setTimeout(() => {
                    debounceTimerRef.current = null;
                    if (!mountedRef.current || !enabledRef.current) return;
                    runTurn(ASSISTANT_EVENT.wizardChanged, extra);
                }, wizardChangedDebounceMs);
                return;
            }
            if (event === ASSISTANT_EVENT.idle) {
                if (pendingQuestionRef.current || isWaitingOnUser(messagesRef.current)) return;
                runTurn(ASSISTANT_EVENT.idle, extra);
                return;
            }
            if (event === ASSISTANT_EVENT.stepEntered) {
                clearTimeout(debounceTimerRef.current);
                runTurn(ASSISTANT_EVENT.stepEntered, extra);
                return;
            }
            runTurn(event, extra);
        },
        [armIdleTimer, runTurn, wizardChangedDebounceMs]
    );

    /** Index of the newest assistant message — only it shows clickable options. */
    const latestAssistantIndex = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            if (messages[i].role === 'assistant') return i;
        }
        return -1;
    }, [messages]);

    return {
        messages,
        pendingQuestion,
        pendingContext,
        loading,
        unavailable,
        offerSupport,
        latestAssistantIndex,
        sendMessage,
        selectOption,
        notifyEvent,
        escalate,
    };
}

export default useSparkAssistant;
